import { Alert, Checkbox, classNames, Spinner, toast } from '@cma/common'
import {
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { HelixIcon } from '@helix/helix-icon'
import { plus as PlusIcon } from '@helix/helix-icon/outlined'
import React, { useEffect, useState } from 'react'
import { useContent } from './ContentContext'
import { ContentFileMenu } from './ContentFileMenu'
import { useDragContent } from './DragContentContext'
import { RenameInput } from './RenameInput'
import { ShareContentModal } from './share_modal/ShareContentModal'
import { useDestroyPlatformClientContents } from './useDestroyPlatformClientContents'
import { useDestroyPlatformClientFolders } from './useDestroyPlatformClientFolders'
import { useUpdatePlatformClientContents } from './useUpdatePlatformClientContents'
import { useUpdatePlatformClientFolder } from './useUpdatePlatformClientFolder'
import { getFileIcon } from './utils'
import { useModalDetection } from './useModalDetection'

interface ContentCardProps {
  item: PlatformClientContentPartsFragment | PlatformClientFolderPartsFragment
}

export function ContentCard({ item }: ContentCardProps) {
  const {
    selectedContents,
    isRenaming,
    hasContentPermissions,
    navigateToFolder,
    setPreviewContent
  } = useContent()
  const { getDraggableFiles, shouldShowAsGhost } = useDragContent()

  const isFolder = item.__typename === 'Folder'
  const isSelected = selectedContents.includes(item.id)
  const isBeingDraggedAsGhost = shouldShowAsGhost(item.id)
  const AssetIcon = getFileIcon(
    item.__typename,
    Boolean(item.contentShares?.length)
  )

  const droppable = useDroppable({
    id: `${item.__typename}-${item.id}`,
    disabled: !isFolder
  })

  const draggable = useDraggable({
    id: `${item.__typename}-${item.id}`,
    disabled: isFolder || isRenaming || !hasContentPermissions
  })

  const setNodeRef = isFolder ? droppable.setNodeRef : draggable.setNodeRef
  const isModalOpen = useModalDetection()

  if (isFolder) {
    return (
      <div
        ref={setNodeRef}
        className={classNames('w-[170px]', {
          'h-[170px] rounded-md ring-2 ring-brand-primary': droppable.isOver,
          'min-h-[210px]': !droppable.isOver
        })}>
        <Card
          id={item.id}
          type={item.__typename}
          title={item?.displayName || ''}
          isSelected={isSelected}
          icon={<AssetIcon style={{ height: 52, width: 68 }} />}
          onClick={() => navigateToFolder(item.id)}
        />
      </div>
    )
  }

  const thisDraggableFiles = getDraggableFiles(item.id)
  const isDragging = draggable.isDragging

  const style: React.CSSProperties = {
    transform: draggable.transform
      ? CSS.Translate.toString(draggable.transform)
      : undefined,

    transition: isDragging ? undefined : 'transform 200ms ease'
  }

  return (
    <div
      ref={setNodeRef}
      {...(isModalOpen ? {} : draggable.listeners)}
      {...(isModalOpen ? {} : draggable.attributes)}
      className={classNames('w-[170px] select-none', {
        'min-h-[210px]': !isDragging,
        'h-[170px]': isDragging,
        'z-50 rotate-3': isDragging,
        'pointer-events-none': isModalOpen && !isFolder,
        'cursor-pointer': !isRenaming,
        'cursor-grabbing': isRenaming,
        hidden: isBeingDraggedAsGhost
      })}
      style={style}>
      <Card
        id={item.id}
        type={item.__typename}
        icon={<AssetIcon style={{ height: 70, width: 55 }} />}
        isSelected={isSelected}
        isDragging={isDragging}
        onClick={() => setPreviewContent(item)}
        title={
          isDragging && thisDraggableFiles.length > 1
            ? thisDraggableFiles
                .map((f) => f.displayName || 'Untitled')
                .join(', ')
            : isDragging
            ? ''
            : item.displayName || ''
        }
      />
      {isDragging && thisDraggableFiles.length > 1 && (
        <div className="absolute -right-2 -top-2 z-30 flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-xs font-bold text-white">
          {thisDraggableFiles.length}
        </div>
      )}
    </div>
  )
}

function Card({
  isSelected = false,
  isDragging = false,
  type = 'PlatformClientContent',
  id,
  title,
  icon,
  onClick
}: {
  id: string
  title: string
  icon: React.ReactNode
  isSelected?: boolean
  isDragging?: boolean
  type: 'Folder' | 'PlatformClientContent'
  onClick?: () => void
}) {
  const {
    currentFolder,
    selectedContents,
    isDeleting,
    isMoving,
    movingFileIds,
    renamingFileId,
    hasContentPermissions,
    toggleFileSelection,
    setIsRenaming,
    setRenamingFileId
  } = useContent()

  const [showSharingModal, setShowSharingModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isRenamingLocal, setIsRenamingLocal] = useState(false)
  const [localName, setLocalName] = useState(title)

  const { mutate: destroyContents, isLoading: isDestroyingContents } =
    useDestroyPlatformClientContents()

  const { mutate: destroyFolder, isLoading: isDestroyingFolder } =
    useDestroyPlatformClientFolders()

  const { mutate: updateFolder, isLoading: isUpdatingFolder } =
    useUpdatePlatformClientFolder()

  const { mutate: updateContents, isLoading: isUpdatingContents } =
    useUpdatePlatformClientContents()

  useEffect(() => {
    if (renamingFileId === id) {
      setIsRenamingLocal(true)
    } else {
      setIsRenamingLocal(false)
    }
  }, [renamingFileId, id])

  const isTypeFolder = type === 'Folder'
  const isSelectedForGroupDelete = selectedContents.includes(id) && isDeleting
  const isSelectedForGroupMove = movingFileIds.includes(id) && isMoving
  const isLoading = isUpdatingContents || isUpdatingFolder

  return (
    <>
      <div className="relative min-h-[210px] w-full">
        {(isDestroyingContents ||
          isSelectedForGroupDelete ||
          isDestroyingFolder) && (
          <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white/80">
            <span className="helix-small--strong text-gray-500">
              Deleting...
            </span>
          </div>
        )}
        {isSelectedForGroupMove && (
          <div className="absolute left-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white/80">
            <span className="helix-small--strong text-gray-500">Moving...</span>
          </div>
        )}
        <div className="group relative flex h-[170px] w-full flex-col overflow-hidden">
          <div className="absolute left-0 top-0 z-10 h-[36px] w-full cursor-pointer rounded-md pl-3 pr-4 transition focus:outline-none">
            <div
              className={classNames('flex h-[36px] justify-between', {
                'invisible group-hover:visible': !isSelected || isLoading,
                visible: isSelected || !isLoading
              })}>
              <div className="flex">
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation()
                    toggleFileSelection(id)
                  }}
                />
              </div>
              {hasContentPermissions && selectedContents?.length <= 1 && (
                <div
                  className={classNames('invisible flex items-center gap-x-4', {
                    'group-hover:visible': !isLoading
                  })}>
                  {!currentFolder && (
                    <button
                      type="button"
                      className="helix-btn helix-btn--primary helix-btn--small text-xs sm:text-sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowSharingModal(true)
                      }}>
                      Share
                    </button>
                  )}
                  <button
                    type="button"
                    tabIndex={0}
                    aria-label="Open file menu"
                    className="flex !w-6 border-none bg-transparent"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        e.stopPropagation()
                      }
                    }}>
                    <ContentFileMenu
                      id={id}
                      type={type}
                      buttonClassName="flex"
                      onDelete={() => setShowDeleteModal(true)}
                      onRename={() => {
                        setIsRenamingLocal(true)
                        setIsRenaming(true)
                      }}
                      onPreview={!isTypeFolder ? onClick : undefined}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div
            role="button"
            tabIndex={0}
            className={classNames(
              'absolute left-0 top-0 z-0 flex h-full w-full cursor-pointer items-center justify-center rounded-md transition focus:outline-none',
              {
                'bg-brand-white group-hover:bg-brand-white': isSelected,
                'bg-gray-50 group-hover:bg-gray-100': !isSelected
              }
            )}
            onClick={(e) => {
              e.stopPropagation()
              onClick?.()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }}>
            <span>{icon}</span>
          </div>
        </div>
        {isRenamingLocal ? (
          <>
            {isLoading ? (
              <div className="mt-2 flex min-h-[24px] items-center justify-center pb-1">
                <span className="helix-small line-clamp-2 block w-full break-words text-gray-400">
                  {localName}
                </span>
                <div className="helix-text-brand h-3.5 w-3.5">
                  <Spinner />
                </div>
              </div>
            ) : (
              <RenameInput
                defaultValue={title}
                onSubmit={(value) => {
                  if (!value.trim()) {
                    return false
                  }
                  setLocalName(value)

                  if (isTypeFolder) {
                    updateFolder(
                      {
                        folderId: id,
                        displayName: value
                      },
                      {
                        onSuccess() {
                          toast(`${value} renamed successfully`, {
                            variant: 'success'
                          })
                        },
                        onError(error) {
                          toast(error.message || 'Failed to rename folder', {
                            variant: 'error'
                          })
                        },
                        onSettled() {
                          setIsRenamingLocal(false)
                          setIsRenaming(false)
                          setRenamingFileId(null)
                        }
                      }
                    )
                  } else {
                    updateContents(
                      {
                        ids: [id],
                        displayName: value
                      },
                      {
                        onSuccess() {
                          toast(`${value} renamed successfully`, {
                            variant: 'success'
                          })
                        },
                        onError(error) {
                          toast(error.message, {
                            variant: 'error'
                          })
                        },
                        onSettled() {
                          setIsRenamingLocal(false)
                          setIsRenaming(false)
                          setRenamingFileId(null)
                        }
                      }
                    )
                  }
                  return true
                }}
                onCancel={() => {
                  setIsRenamingLocal(false)
                  setIsRenaming(false)
                  setRenamingFileId(null)
                }}
              />
            )}
          </>
        ) : (
          <span
            role={!isTypeFolder ? 'button' : undefined}
            tabIndex={!isTypeFolder ? 0 : undefined}
            onClick={
              !isTypeFolder
                ? (e) => {
                    e.stopPropagation()
                    onClick?.()
                  }
                : undefined
            }
            onKeyDown={
              !isTypeFolder
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onClick?.()
                    }
                  }
                : undefined
            }
            className={classNames(
              'helix-small mt-2 block w-full break-words text-gray-600',
              { 'cursor-pointer': !isTypeFolder }
            )}
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden'
            }}>
            {isDragging ? '' : title}
          </span>
        )}
      </div>
      <ShareContentModal
        ids={[{ id, type }]}
        open={showSharingModal}
        onClose={() => setShowSharingModal(false)}
      />
      <Alert variant="danger" isOpen={showDeleteModal}>
        <Alert.Title>Delete {isTypeFolder ? 'Folder' : 'Files'}</Alert.Title>
        <Alert.Content>
          Are you sure you want to delete (
          <strong className="font-medium text-gray-700">{title}</strong>
          )? This action cannot be undone.
        </Alert.Content>
        <Alert.Cancel onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Alert.Cancel>
        <Alert.Confirm
          loading={isTypeFolder ? isDestroyingFolder : isDestroyingContents}
          onClick={() => {
            if (isTypeFolder) {
              destroyFolder(
                { ids: [id] },
                {
                  onSuccess() {
                    toast(`${title} deleted successfully`, {
                      variant: 'success'
                    })
                  },
                  onError(error) {
                    toast(error.message || 'Failed to delete folder', {
                      variant: 'error'
                    })
                  }
                }
              )
            } else {
              destroyContents(
                { ids: [id] },
                {
                  onSuccess() {
                    toast(`${title} deleted successfully`, {
                      variant: 'success'
                    })
                  },
                  onError(error) {
                    toast(error.message, {
                      variant: 'error'
                    })
                  }
                }
              )
            }
            setShowDeleteModal(false)
          }}>
          Delete Forever
        </Alert.Confirm>
      </Alert>
    </>
  )
}

interface UploadCardProps {
  disabled?: boolean
  uploadRef: React.RefObject<HTMLInputElement>
}

export function UploadCard({ disabled = false, uploadRef }: UploadCardProps) {
  return (
    <div
      role="button"
      tabIndex={!disabled ? 0 : -1}
      className={classNames(
        'relative flex h-[170px] w-[150px] cursor-pointer flex-col items-center justify-center rounded-lg px-3 py-4 transition focus:outline-none sm:w-[170px]',
        { 'cursor-not-allowed opacity-50': disabled }
      )}
      onClick={() => !disabled && uploadRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          uploadRef.current?.click()
        }
      }}>
      <span className="-mt-6">
        <HelixIcon
          icon={PlusIcon}
          className={classNames('h-8 w-8', {
            'helix-svg-fill--green-700': !disabled,
            'text-gray-400': disabled
          })}
        />
      </span>
      <div
        className={classNames(
          'absolute inset-0 flex h-full w-full items-center justify-center rounded-md !border !border-dashed',
          {
            '!border-gray-300 hover:!border-brand-light': !disabled,
            '!border-gray-200': disabled
          }
        )}>
        <span
          className={classNames('helix-body--strong mt-12 text-center', {
            'text-gray-600': !disabled,
            'text-gray-400': disabled
          })}>
          Upload
        </span>
      </div>
    </div>
  )
}
